import type { LangMap } from './lang';

export interface Landmark {
  id: number;
  lng: number;
  lat: number;
  mercX?: number;
  mercY?: number;
  x?: number;
  y?: number;
  isPublished: boolean;
  order: number | null;
  slug: string;
  name: LangMap;
  description: LangMap;
}
export interface LandmarkCategory {
  /** IconStyle と紐づけるための styleID 的なもの */
  styleUrl: string | null;
  name: LangMap;
  order: number | null;
  landmark: Landmark[];
}
export interface LandmarkCategories {
  [categoryKey: string]: LandmarkCategory;
}

type MapDocument = {
  defLang: string;
  title: LangMap;
  landmarkCategories: LandmarkCategories;
};

type MapLoadResult = {
  status: number;
  mapDocument: MapDocument;
};

export function mapDocumentToLandmarks(document: MapDocument): Landmark[] {
  const landmarks: Landmark[] = [];
  for (const category of Object.values(document.landmarkCategories)) {
    for (const landmark of category.landmark) {
      landmarks.push(landmark);
    }
  }
  return landmarks;
}

export function getLandmarksObject(
  document: MapDocument,
): Record<string, Landmark> {
  const landmarks: Record<string, Landmark> = {};
  for (const category of Object.values(document.landmarkCategories)) {
    for (const landmark of category.landmark) {
      landmarks[landmark.id] = landmark;
    }
  }
  return landmarks;
}

export async function loadKml(kmlUrl: string): Promise<MapLoadResult> {
  const url = `${kmlUrl}?${new Date().getTime()}`;

  return fetch(url)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }
      return response.text();
    })
    .then((text) => {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(text, 'application/xml');
      const mapDocument = getDataFromKmlDom(xmlDoc);
      return {
        status: 200,
        mapDocument,
      };
    })
    .catch((error) => {
      console.error('Error loading KML:', error);
      throw new Error(`Failed to load KML: ${error.message}`);
    });
}

// Kind of polyfill for the missing document.evaluate in all versions of IE
export function getElementsIE(
  document: Document,
  root: Node,
  xpathExpression: string,
): Node[] {
  // Need to remove the xml namespaces
  const tagName = xpathExpression
    .replace(/^kml:/, '')
    .replace(/^illustmap:/, '')
    .replace(/^dcterms:/, '');

  let elements = (root as Element).getElementsByTagName(tagName);
  // If we can't find anything
  if (elements.length === 0) {
    elements = (root as Element).getElementsByTagName(xpathExpression);
  }

  const nodes: Node[] = [];

  for (let i = 0; i < elements.length; i++) {
    if (elements[i].parentNode === root) {
      nodes.push(elements[i]);
    }
  }

  return nodes;
}

function nsResolver(prefix: string | null): string | null {
  if (!prefix) return null;
  const ns: Record<string, string> = {
    kml: 'http://earth.google.com/kml/2.1',
    illustmap: 'http://illustmap.org/ns/2.0',
    dcterms: 'http://purl.org/dc/terms/',
  };
  return ns[prefix] || null;
}

// Gets all nodes in order that match the xpath
export function getElements(
  document: Document,
  root: Node,
  xpathExpression: string,
): Node[] {
  // document.evaluate is available everywhere but in IE and allows to do
  // xpath evaluation directly
  if (document.evaluate) {
    const xpathResult = document.evaluate(
      xpathExpression,
      root,
      nsResolver,
      XPathResult.ORDERED_NODE_SNAPSHOT_TYPE,
      null,
    );
    const results: Node[] = [];
    for (let i = 0; i < xpathResult.snapshotLength; i++) {
      const node = xpathResult.snapshotItem(i);
      if (node) {
        results.push(node);
      }
    }
    return results;
  }

  // IE support
  return getElementsIE(document, root, xpathExpression);
}

// Gets the first element from the document matching the xpath
export function getElement(
  document: Document,
  root: Node,
  xpathExpression: string,
): Node | null {
  const nodes = getElements(document, root, xpathExpression);
  if (nodes.length > 0) {
    return nodes[0];
  }
  return null;
}

// Grabs the content for the given nodes for each lang
function getContentByLang(
  nodes: Node[],
  defaultLang: string,
): { [key: string]: string } {
  const data: Record<string, string> = {};
  for (let i = 0; i < nodes.length; i++) {
    const nodeLang: string =
      (nodes[i] as Element).getAttribute('xml:lang') || defaultLang;
    data[nodeLang] = nodes[i].textContent || '';
  }
  return data;
}

export function getDataFromKmlDom(document: Document | string): MapDocument {
  if (typeof document === 'string') {
    const parser = new DOMParser();
    document = parser.parseFromString(document, 'application/xml');
  }

  const kDoc = getElement(document, document.documentElement, 'kml:Document');
  if (!kDoc) {
    throw new Error('Invalid KML: Document element not found');
  }

  // defLang
  const lang = document.documentElement.getAttribute('xml:lang') || 'ja';
  const defLang = lang;

  // Title(s)
  const title = getContentByLang(
    getElements(document, kDoc, 'illustmap:title'),
    lang,
  );

  const folders = getElements(document, kDoc, 'kml:Folder');
  const mappingLayersFolders: Node[] = [];
  const landmarksFolders: Node[] = [];
  for (let i = 0; i < folders.length; i++) {
    const folderKind = (folders[i] as Element).getAttribute('type');
    if (folderKind === 'illustmap') {
      mappingLayersFolders.push(folders[i]);
    } else if (folderKind === 'landmark') {
      landmarksFolders.push(folders[i]);
    }
  }

  const landmarkCategories = getLandmarkCategories(
    document,
    landmarksFolders,
    lang,
  );

  return {
    defLang,
    title,
    landmarkCategories,
  };
}

function getLandmarkCategories(
  document: Document,
  landmarksFolders: Node[],
  lang: string,
) {
  const landmarkDataCollection: LandmarkCategories = {};

  for (let i = 0; i < landmarksFolders.length; i++) {
    const id = (landmarksFolders[i] as Element).getAttribute('id');
    if (!id) continue; // IDが無い場合はスキップ

    landmarkDataCollection[id] = {
      ...landmarkDataCollection[id],
      styleUrl: null,
      landmark: [],
      name: getContentByLang(
        getElements(document, landmarksFolders[i], 'kml:name'),
        lang,
      ),
    };

    const styleUrl = getElement(document, landmarksFolders[i], 'kml:styleUrl');
    if (styleUrl && styleUrl.textContent) {
      const val = styleUrl.textContent.split('#');
      if (val && val.length > 0) {
        if (id) {
          landmarkDataCollection[id].styleUrl = val.pop() ?? null;
        }
      }
    }

    const placemarks = getElements(
      document,
      landmarksFolders[i],
      'kml:Placemark',
    );

    const categoryOrderElement = getElements(
      document,
      landmarksFolders[i],
      'kml:order',
    );
    const categoryOrderText = categoryOrderElement.length
      ? categoryOrderElement[0].textContent
      : 'null';
    if (id) {
      landmarkDataCollection[id].order =
        categoryOrderText === 'null' ? null : Number(categoryOrderText);
    }

    for (let j = 0; j < placemarks.length; j++) {
      const landmark: Record<string, unknown> = {
        name: getContentByLang(
          getElements(document, placemarks[j], 'kml:name'),
          lang,
        ),
        description: getContentByLang(
          getElements(document, placemarks[j], 'kml:description'),
          lang,
        ),
      };
      const landmarkID = (placemarks[j] as Element).getAttribute('id');
      if (!landmarkID) {
        continue; // IDがない場合は次のループへ
      }
      landmark.id = parseInt(landmarkID);

      const point = getElement(document, placemarks[j], 'kml:Point');
      const lnglat = point?.textContent?.split(',');
      if (lnglat && lnglat.length >= 2) {
        landmark.lng = lnglat[0];
        landmark.lat = lnglat[1];
      }
      const xy = point ? getElement(document, point, 'illustmap:xy') : null;
      if (xy && xy.textContent) {
        const vals = xy.textContent.split(',');
        landmark.x = vals[0];
        landmark.y = vals[1];
      }
      const mercXY = point
        ? getElement(document, point, 'illustmap:mercator_xy')
        : null;
      if (mercXY && mercXY.textContent) {
        const vals = mercXY.textContent.split(',');
        landmark.mercX = vals[0];
        landmark.mercY = vals[1];
      }

      const isPublished = point
        ? getElement(document, point, 'kml:isPublished')
        : null;
      landmark.isPublished = isPublished
        ? isPublished.textContent?.toLowerCase() === 'false'
          ? false
          : true
        : true;

      const orderElement = point
        ? getElement(document, point, 'kml:order')
        : null;
      const orderText = orderElement ? orderElement.textContent : 'null';
      landmark.order = orderText === 'null' ? null : Number(orderText);

      const slugElement = point
        ? getElement(document, point, 'kml:slug')
        : null;
      landmark.slug = slugElement ? slugElement.textContent : '';

      if (id) {
        landmarkDataCollection[id].landmark.push(landmark as any);
      }
    }
  }

  return landmarkDataCollection;
}
